/**
 * This script manages the custom cart drawer for a Shopify store.
 *
 * Warranty (Extended Warranty) Logic:
 * - Users can add one warranty product (2, 3, or 4 years) to their cart.
 * - If a warranty is already in the cart, adding another warranty will replace the existing one.
 * - The warranty form collects order information (e.g., order number, pc code).
 * - When the warranty form is submitted, the warranty line item is updated with the new properties.
 * - Users can edit warranty details by clicking the edit icon, which reveals the warranty form.
 * - The warranty form is only visible if there is only a warranty product in the cart (no other products).
 *
 * Peace of Mind Shipping Insurance Logic:
 * - Users can add or remove shipping insurance via a toggle/checkbox in the cart drawer.
 * - Only one insurance item can be in the cart at a time; if quantity > 1, it is reset to 1.
 * - Insurance is automatically added to the cart if the cart is empty and an item is added.
 * - Removing insurance updates the cart and UI accordingly.
 *
 * Both features are tightly integrated with the cart's state and UI updates, ensuring a smooth user experience.
 *
 * - Hardcoded Product IDs:
 *   Warranty and insurance product IDs are hardcoded. If these change in Shopify, update them here as well.
 *
 * - Full Cart Drawer Re-render:
 *   After most cart actions, the entire cart drawer HTML is re-fetched and replaced. All event listeners must be re-attached after each update.
 *   This ensures UI consistency.
 *
 * - Warranty Form Visibility:
 *   The warranty form is only shown if the cart contains only a warranty product (no other products).
 *
 * - Warranty Edit Flow:
 *   Editing warranty details removes the warranty product from the cart and re-adds it with new properties.
 *
 * - Insurance & Warranty Only Rule:
 *   If the cart contains only insurance and warranty, insurance is automatically removed (see checkOnlyInsuranceAndWarrantyInCart).
 *
 */

document.addEventListener("DOMContentLoaded", function() {
    const drawerButton = document.querySelector(
        "#custom-cart-drawer-open-close-btn"
    );

    const INSURANCE_PRODUCT_ID = 43298768060596;
    const EXTENDED_WARRATY_2_YEARS_ID = 43299024011444;
    const EXTENDED_WARRATY_3_YEARS_ID = 43299024994484;
    const EXTENDED_WARRATY_4_YEARS_ID = 43299025027252;

    const warrantyProductIds = [
        EXTENDED_WARRATY_2_YEARS_ID,
        EXTENDED_WARRATY_3_YEARS_ID,
        EXTENDED_WARRATY_4_YEARS_ID,
    ];

    const drawerModal = document.querySelector(
        ".cart-drawer__product-modal-container"
    );

    const drawerOuter = document.querySelector(".cart-drawer__outer");
    const drawerInner = document.querySelector(".cart-drawer__inner");
    const isInsuranceEnabled = drawerOuter.dataset.isInsuranceEnabled === "true";

    const atcButtons = document.querySelectorAll(".atcBtnDrawer");

    const addInsuranceAutomaticallyState = {
        isInsuranceAdded: false,
        isCartEmpty: drawerOuter.dataset.isEmpty === "true",
    };

    const cartState = {
        initialCart: JSON.parse(drawerOuter.dataset.cartJson),
        currentCart: JSON.parse(drawerOuter.dataset.cartJson),
    };
    
    const cableSocketState = {
        map: {},
        isLoaded: false,
    };

    const upsellState = {
        scrollLeft: 0,
        scrollTop: 0,
        windowWidth: window.innerWidth,
    };
    // Declared before INIT: functions below run during init and would hit
    // the temporal dead zone if these lived next to them.
    // - one shared in-flight cableSocketMap.json load
    let cableSocketMapPromise = null;
    // - several buttons/SKUs often map to the same cable product: fetch each
    //   handle once per page load
    const productByHandleRequests = {};
    // - serialised drawer re-renders (see updateCartHTML)
    let cartRenderInFlight = null;
    let cartRenderQueued = null;

    // INIT DRAWER START
    attachAtcEvents();
    attachDrawerEvents();
    attachCartLineItemsEvents();
    attachInsuranceCheckboxEvents();
    attachCheckoutButtonEvents();
    attachWarrantyFormEvents();
    attachUpsellEvents();
    checkSkuAvailability();
    // A cart left holding only the shipping insurance (or insurance +
    // warranty), e.g. from an earlier visit, is cleaned up right away.
    if (getIsInsuranceOnlyItemInCart() || checkOnlyInsuranceAndWarrantyInCart()) {
        updateCartHTML();
    }
    // INIT DRAWER END




    // GET + parse JSON. Returns null on any failure (callers treat null as
    // "not available", same as before). Network errors and 5xx/429 get one
    // retry so a single dropped request doesn't flip a button to Out of
    // Stock; a real 404 (no such product) is final.
    async function fetchJson(url, retries = 1) {
        try {
            const response = await fetch(url);

            if (!response.ok) {
                const isRetryable = response.status >= 500 || response.status === 429;
                if (isRetryable && retries > 0) return fetchJson(url, retries - 1);
                throw new Error(`Fetch failed (${response.status}): ${url}`);
            }

            return await response.json();
        } catch (error) {
            if (error instanceof TypeError && retries > 0) {
                // network-level failure (offline blip, aborted connection)
                return fetchJson(url, retries - 1);
            }
            console.error("fetchJson error:", error);
            return null;
        }
    }

    function loadCableSocketMap(url) {
        if (!url) {
            console.warn("Cable socket map URL not found");
            return Promise.resolve({});
        }

        if (!cableSocketMapPromise) {
            cableSocketMapPromise = fetchJson(url).then((map) => {
                if (!map) {
                    cableSocketState.map = {};
                    cableSocketState.isLoaded = false;
                    cableSocketMapPromise = null; // allow a later retry
                    return {};
                }

                cableSocketState.map = map;
                cableSocketState.isLoaded = true;
                return map;
            });
        }

        return cableSocketMapPromise;
    }

    function getMappedSku(sku, map = cableSocketState.map) {
        if (!sku) return null;

        const normalizedSku = sku.trim().toUpperCase();

        return map[normalizedSku] || normalizedSku;
    }

    function getProductHandleFromSku(sku) {
        if (!sku) return null;

        return sku.trim().toLowerCase();
    }

    function getProductByHandle(handle) {
        if (!handle) return Promise.resolve(null);

        if (!productByHandleRequests[handle]) {
            const productUrl = `${Shopify.routes.root}products/${handle}.js`;
            productByHandleRequests[handle] = fetchJson(productUrl);
        }

        return productByHandleRequests[handle];
    }

    function isProductAvailable(product) {
        if (!product || !Array.isArray(product.variants)) {
            return false;
        }

        return product.variants.some((variant) => variant.available);
    }

    function updateAvailabilityUI(buyButton, isAvailable) {
        const outOfStockEl = buyButton.nextElementSibling;

        if (isAvailable) {
            buyButton.style.display = "inline-flex";

            if (outOfStockEl) {
                outOfStockEl.style.display = "none";
            }
        } else {
            buyButton.style.display = "none";

            if (outOfStockEl) {
                outOfStockEl.style.display = "inline-flex";
            }
        }
    }

    async function checkSingleSkuAvailability({
        sku,
        map = cableSocketState.map,
    }) {
        const mappedSku = getMappedSku(sku, map);
        const productHandle = getProductHandleFromSku(mappedSku);
        const product = await getProductByHandle(productHandle);

        const available = isProductAvailable(product);

        return {
            originalSku: sku,
            mappedSku,
            productHandle,
            product,
            available,
        };
    }

    async function checkSkuAvailability({
        selector = ".atcBtnDrawer[data-sku]",
        mapUrl = drawerOuter.dataset.cableStock,
        map = null,
    } = {}) {
        const buyButtons = document.querySelectorAll(selector);
        if (!buyButtons.length) return;

        const cableMap = map || (
            cableSocketState.isLoaded ?
            cableSocketState.map :
            await loadCableSocketMap(mapUrl)
        );

        // All buttons in parallel; each one updates as soon as its own
        // answer arrives (same per-button rule as before).
        await Promise.all(
            Array.from(buyButtons).map(async (buyButton) => {
                const result = await checkSingleSkuAvailability({
                    sku: buyButton.dataset.sku,
                    map: cableMap,
                });
                updateAvailabilityUI(buyButton, result.available);
            })
        );
    }


    function attachCartLineItemsEvents() {
        const stepperButtons = document.querySelectorAll(
            ".cart-drawer__stepper-button"
        );

        // This adds event listeners to the stepper buttons for incrementing and decrementing the quantity of items in the cart.
        stepperButtons.forEach((button) => {
            const role = button.dataset.role; // either 'increment' or 'decrement'
            const lineItem = button.closest(".cart-drawer__line-item");
            const lineItemId = lineItem.dataset.lineItemId;
            button.addEventListener("click", async function() {
                const itemCount = lineItem.querySelector(
                    ".cart-drawer__stepper-count"
                ).textContent;
                let quantity = parseInt(itemCount, 10);

                if (role === "increment") {
                    quantity += 1;
                } else if (role === "decrement") {
                    quantity -= 1;
                }

                if (quantity < 0) {
                    quantity = 0;
                }
                const count = lineItem.querySelector(".cart-drawer__stepper-qty");
                const loader = lineItem.querySelector(
                    ".cart-drawer__stepper-count-loader-container"
                );
                count.style.opacity = 0;
                loader.classList.remove("hidden");
                const result = await updateCartLine(lineItemId, quantity);
            });
        });

        const removeButtons = document.querySelectorAll(
            ".cart-drawer__line-item-icon-container.delete-icon"
        );
        removeButtons.forEach((button) => {
            button.addEventListener("click", async function() {
                const lineItem = button.closest(".cart-drawer__line-item");
                const lineItemId = lineItem.dataset.lineItemId;
                const iconContainer = lineItem.querySelector(
                    ".cart-drawer__line-item-delete-icon"
                );
                iconContainer.style.opacity = 0;
                const loader = lineItem.querySelector(
                    ".cart-drawer__line-item-delete-loading-container"
                );
                loader.classList.remove("hidden");
                await removeItemFromCart(lineItemId);
            });
        });
    }

    async function updateCartLine(lineItemId, quantity) {
        return await fetch(`${Shopify.routes.root}cart/update.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify({
                    updates: {
                        [lineItemId]: quantity,
                    },
                }),
            })
            .then((response) => response.json())
            .then(async (data) => {
                await updateCartHTML();
                return data;
            })
            .catch((error) => {
                console.error("Error:", error);
            });
    }

    // Cart re-renders are serialised: a call made while one is running queues
    // exactly one more (which fetches fresh state), so overlapping calls can
    // never paint an older cart over a newer one, and bursts collapse into a
    // single extra request.
    function updateCartHTML() {
        if (!cartRenderInFlight) {
            cartRenderInFlight = renderCartHTML().finally(() => {
                cartRenderInFlight = null;
            });
            return cartRenderInFlight;
        }
        if (!cartRenderQueued) {
            cartRenderQueued = cartRenderInFlight
                .catch(() => {})
                .then(() => {
                    cartRenderQueued = null;
                    return updateCartHTML();
                });
        }
        return cartRenderQueued;
    }

    async function fetchDrawerSection() {
        const fetchedDrawerRaw = await fetch(
            `${Shopify.routes.root}?sections=custom-cart-drawer-v2`
        ).then((res) => res.json());
        const parsedDrawer = new DOMParser().parseFromString(
            fetchedDrawerRaw["custom-cart-drawer-v2"],
            "text/html"
        );
        const fetchedDrawerInner = parsedDrawer.querySelector(
            ".cart-drawer__inner"
        );
        fetchedDrawerInner.classList.add("visible");

        const fetchedDrawerOuter = parsedDrawer.querySelector(
            ".cart-drawer__outer"
        );
        cartState.currentCart = JSON.parse(fetchedDrawerOuter.dataset.cartJson);
        return fetchedDrawerOuter;
    }

    // Shipping insurance never stays in the cart without a product to insure:
    // removed when it's left alone (the drawer/header already treat that cart
    // as empty) or left with only the warranty. Returns true if it removed it.
    async function removeOrphanedInsurance() {
        const isInsuranceOnly = getIsInsuranceOnlyItemInCart();
        if (!isInsuranceOnly && !checkOnlyInsuranceAndWarrantyInCart()) {
            return false;
        }
        await removeItemFromCart(INSURANCE_PRODUCT_ID, true);
        if (isInsuranceOnly) {
            // cart is really empty now: the next add-to-cart may auto-add
            // insurance again, exactly like a fresh empty cart
            addInsuranceAutomaticallyState.isCartEmpty = true;
            addInsuranceAutomaticallyState.isInsuranceAdded = false;
        }
        return true;
    }

    // Never call updateCartHTML() from in here — it would wait on itself.
    async function renderCartHTML() {
        let fetchedDrawerOuter = await fetchDrawerSection();

        if (await removeOrphanedInsurance()) {
            fetchedDrawerOuter = await fetchDrawerSection();
        }

        drawerOuter.innerHTML = fetchedDrawerOuter.innerHTML;
        updateCartItemCountOnHeader();
        attachCartLineItemsEvents();
        attachInsuranceCheckboxEvents();
        attachCheckoutButtonEvents();
        attachWarrantyFormEvents();
        attachDrawerEvents();
        attachUpsellEvents();
    }

    function updateCartItemCountOnHeader() {
        // c-header badge: insurance alone counts as an empty cart
        const cHeaderCountElement = document.querySelector(".c-header__cart-count");
        if (cHeaderCountElement) {
            const count = getIsInsuranceOnlyItemInCart() ?
                0 :
                cartState.currentCart.item_count;
            cHeaderCountElement.innerText = count;
            cHeaderCountElement.hidden = count === 0;
        }

        const headerCountElement = document.querySelector(
            ".custom-cart-drawer-open-close-count"
        );
        if (headerCountElement) {
            headerCountElement.innerText = cartState.currentCart.item_count;
            const isInsuranceOnlyItemInCart = getIsInsuranceOnlyItemInCart();
            if (isInsuranceOnlyItemInCart) {
                headerCountElement.style.opacity = 0;
                return;
            }
            cartState.currentCart.item_count === 0 ?
                (headerCountElement.style.opacity = 0) :
                (headerCountElement.style.opacity = 1);
        }
    }

    function getIsInsuranceOnlyItemInCart() {
        const cartItems = cartState.currentCart.items;
        const insuranceInCart = cartItems.find((item) => {
            return item.id === INSURANCE_PRODUCT_ID;
        });

        if (cartItems.length === 1 && insuranceInCart) {
            return true;
        }
        return false;
    }

    function checkOnlyInsuranceAndWarrantyInCart() {
        // Early return if cart do not have 2 items
        if (cartState.currentCart.items.length !== 2) return false;
        const cartItems = cartState.currentCart.items;
        const insuranceInCart = cartItems.find((item) => {
            return item.id === INSURANCE_PRODUCT_ID;
        });
        const warrantyInCart = cartItems.find((item) => {
            return warrantyProductIds.includes(item.id);
        });
        if (!insuranceInCart || !warrantyInCart) return false;
        return true;
    }

    drawerButton.addEventListener("click", function() {
        openDrawer();
    });
    drawerOuter.addEventListener("click", function(e) {
        if (e.target === drawerOuter) {
            closeDrawer();
        }
    });

    function attachDrawerEvents() {
        const drawerClosers = document.querySelectorAll(
            "[data-role='close-drawer']"
        );
        drawerClosers.forEach((closer) => {
            closer.addEventListener("click", function() {
                closeDrawer();
            });
        });
    }

    function makeLoadingVisible(button) {
        const text = button.querySelector(".text");
        const loader = button.querySelector(".loader-container");
        if (!text || !loader) return;
        text.style.opacity = 0;
        loader.classList.remove("hidden");
        // BACKUP HIDE LOADING
        setTimeout(() => {
            hideLoading(button);
        }, 10000);
    }

    function hideLoading(button) {
        const text = button.querySelector(".text");
        const loader = button.querySelector(".loader-container");
        if (!text || !loader) return;
        text.style.opacity = 1;
        loader.classList.add("hidden");
    }

    function attachCheckoutButtonEvents() {
        const button = document.querySelector(".cart-drawer__checkout-button");
        button?.addEventListener("click", async function(e) {
            e.preventDefault();
            const shouldPrevent = this.dataset.preventCheckout;
            if (shouldPrevent) return;
            makeLoadingVisible(this);
            await validateInsuranceCount();
            window.location.href = this.href;
        });
    }

    async function validateInsuranceCount() {
        const insuranceLine = cartState.currentCart.items.find((item) => {
            return item.id === INSURANCE_PRODUCT_ID;
        });

        if (!insuranceLine) return;
        const insuranceQuantity = insuranceLine.quantity;
        if (insuranceQuantity > 1) {
            const formData = {
                updates: {
                    [insuranceLine.id]: 1,
                },
            };
            await fetch(`${Shopify.routes.root}cart/update.js`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                    },
                    body: JSON.stringify(formData),
                })
                .then((response) => response.json())
                .then((data) => {
                    updateCartHTML();
                })
                .catch((error) => {
                    console.error("Error:", error);
                });
        }
    }

    function attachWarrantyFormEvents() {
        const warrantyFormButton = document.querySelector(
            ".cart-drawer__warranty-button"
        );
        warrantyFormButton?.addEventListener("click", async function(e) {
            e.preventDefault();
            const isFormValid = validateWarrantyForm();
            if (!isFormValid) return;
            const form = document.querySelector(
                ".cart-drawer__warranty-form-container"
            );
            const formData = new FormData(form);
            const button = form.querySelector(".cart-drawer__warranty-button");
            button.classList.add("loading");
            const currentWarrantyInCart = cartState.currentCart.items[0];

            const warrantyLabels = getWarrantyLabels();

            // Maps form data keys to warranty labels and creates an object with the mapped keys and values.
            const firstPropertyKey = Object.keys(currentWarrantyInCart.properties)[0];
            const firstPropertyValue =
                currentWarrantyInCart.properties[firstPropertyKey];
            const properties = {
                [firstPropertyKey]: firstPropertyValue,
                ...Object.fromEntries(
                    Array.from(formData.entries()).map(([key, value]) => [
                        warrantyLabels[key],
                        value,
                    ])
                ),
            };

            await removeItemFromCart(currentWarrantyInCart.id, true);
            addItemToCart(currentWarrantyInCart.id, 1, properties);

            function getWarrantyLabels() {
                const inputs = form.querySelectorAll("input");
                const labels = {};
                inputs.forEach((input) => {
                    const label = input
                        .closest(".cart-drawer__warranty-input-container")
                        .querySelector("label");
                    labels[input.name] = label.innerText;
                });
                return labels;
            }
        });

        const warrantyEditIcon = document.querySelector(
            ".cart-drawer__line-item-icon-container"
        );

        warrantyEditIcon?.addEventListener("click", function() {
            const warrantyCard = document.querySelector(
                ".cart-drawer__warranty-info-card-container"
            );
            if (!warrantyCard) return;
            warrantyCard.classList.remove("hidden");
        });
    }

    function attachInsuranceCheckboxEvents() {
        const insuranceCheckbox = document.querySelector(
            "input[name='shipping-insurance']"
        );
        if (!insuranceCheckbox) return;
        insuranceCheckbox.addEventListener("click", handleInsuranceCheckboxChange);
    }

    // Buttons with their own .icon_text + .loader (shadow-product-gallery) swap
    // those; every other .atcBtnDrawer gets the CSS spinner from .is-loading.
    function setAtcLoading(button, isLoading) {
        button.classList.toggle("is-loading", isLoading);
        button.setAttribute("aria-busy", isLoading ? "true" : "false");
        const text = button.querySelector(".icon_text");
        const loader = button.querySelector(".loader");
        if (text && loader) {
            text.classList.toggle("invisible", isLoading);
            loader.classList.toggle("invisible", !isLoading);
        }
    }

    function attachAtcEvents() {
        atcButtons.forEach((button) => {
            button.addEventListener("click", async function(event) {
                if (this.classList.contains("is-loading")) return;

                const itemId = this.dataset.atc;
                const selection = this.dataset.selection;

                if (window.location.pathname.includes("/products/extended-warranty")) {
                    const shouldProceed = validateWarrantyAddition(event);
                    if (!shouldProceed) return;
                }

                const formData = {
                    items: [{
                        id: itemId,
                        quantity: 1,
                        properties: {
                            Selection: selection,
                        },
                    }, ],
                };
                setAtcLoading(this, true);
                await fetch(`${Shopify.routes.root}cart/add.js`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                        },
                        body: JSON.stringify(formData),
                    })
                    .then((response) => response.json())
                    .then(async (data) => {
                        // keep the spinner until the drawer HTML is refreshed
                        await updateCartHTML();
                        openDrawer();
                        const shouldAddInsurance = getShouldAddInsuranceAutomatically();
                        if (shouldAddInsurance) {
                            addShippingInsuranceToCart();
                        }
                    })
                    .catch((error) => {
                        console.error("Error:", error);
                    })
                    .finally(() => {
                        // Remove Loading
                        setAtcLoading(this, false);
                        this.querySelector(".unique_atc_text")?.classList?.remove("hidden");
                        this.querySelector(".loading__spinner")?.classList?.add("hidden");
                    });
            });
        });
    }

    function attachUpsellEvents() {
        const upsellVariantPickers = document.querySelectorAll(
            ".cart-drawer__upsell-item-select"
        );

        upsellVariantPickers.forEach((picker) => {
            picker.addEventListener("change", function() {
                const item = this.closest(".cart-drawer__upsell-item");
                // Change the price based on the selected option
                const selectedOption = this.options[this.selectedIndex];
                const price = selectedOption.dataset.price;
                const atcButton = item.querySelector(
                    ".cart-drawer__upsell-item-atc-button"
                );

                // Change the image based on the selected option
                const imageElement = item.querySelector(
                    ".cart-drawer__upsell-item-image"
                );
                const image = selectedOption.dataset.variantImage;
                imageElement.src = image;
                imageElement.srcset = image;

                const moneySpan = item.querySelector(".money");
                atcButton.dataset.atc = this.value;
                atcButton.dataset.selection = selectedOption.innerText.trim();
                moneySpan.innerText = price;
            });
        });

        const upsellAtcButtons = document.querySelectorAll(
            ".cart-drawer__upsell-item-atc-button"
        );
        upsellAtcButtons.forEach((button) => {
            button.addEventListener("click", async function(event) {
                const loader = this.querySelector(
                    ".cart-drawer__upsell-item-atc-button-loading-container"
                );
                const innerContainer = this.querySelector(
                    ".cart-drawer__upsell-item-atc-button-inner"
                );

                loader.classList.remove("hidden");
                innerContainer.style.opacity = 0;
                const itemId = this.dataset.atc;
                const selection = this.dataset.selection;
                const warrantyInCart = isWarrantyInCart();
                const isClickedItemWarranty = warrantyProductIds.includes(
                    parseInt(itemId)
                );
                if (isClickedItemWarranty && warrantyInCart) {
                    const fountWarrantyInCart = cartState.currentCart.items.find(
                        (item) => {
                            return warrantyProductIds.includes(item.id);
                        }
                    );
                    const warrantyId = fountWarrantyInCart.id;
                    if (warrantyId === itemId) {
                        // Early return if the clicked warranty is already in the cart
                        loader.classList.add("hidden");
                        innerContainer.style.opacity = 1;
                        return;
                    }
                    await removeItemFromCart(warrantyId, true);
                }
                addItemToCart(itemId, 1, {
                    Selection: selection,
                });
            });
        });

        const upsellItemsContainer = document.querySelector(
            ".cart-drawer__upsell-container"
        );
        const upsellItemsWrapper = document.querySelector(
            ".cart-drawer__upsell-items-wrapper"
        );
        if (upsellItemsWrapper && upsellItemsContainer) {
            upsellItemsWrapper.addEventListener(
                "scroll",
                function handleUpsellXScroll() {
                    const scrollLeft = this.scrollLeft;
                    upsellState.scrollLeft = scrollLeft;
                }
            );

            upsellItemsContainer.addEventListener(
                "scroll",
                function handleUpsellYScroll() {
                    const scrollTop = this.scrollTop;
                    upsellState.scrollTop = scrollTop;
                }
            );
            upsellItemsWrapper.scrollTo({
                left: upsellState.scrollLeft,
                behavior: "instant",
            });

            upsellItemsContainer.scrollTo({
                top: upsellState.scrollTop,
                behavior: "instant",
            });
        }

        const upsellItemsCovers = document.querySelectorAll(
            ".cart-drawer__upsell-item-image-overlay"
        );
        upsellItemsCovers.forEach((cover) => {
            const imageContainer = cover.closest(
                ".cart-drawer__upsell-item-image-container"
            );
            imageContainer.addEventListener("click", async function() {
                const item = this.closest(".cart-drawer__upsell-item");
                const title =
                    item.querySelector(".cart-drawer__upsell-item-title")?.textContent ||
                    "";
                const images = cover.dataset.imageUrls
                    .split(",")
                    .map((img) => img.trim());
                prepareUpsellModal({
                    title,
                    images,
                });
                openUpsellItemModal();
            });
        });
    }

    function prepareUpsellModal(payload) {
        const isNavInserted = document.querySelector(
            ".cart-drawer__product-modal-swiper .swiper-button-next"
        );
        if (!isNavInserted) {
            document.querySelector(
                ".cart-drawer__product-modal-swiper-nav"
            ).innerHTML = `<div class="swiper-button-next"></div><div class="swiper-button-prev"></div>`;
        }
        const {
            title,
            images
        } = payload;
        const titleElement = drawerModal.querySelector(
            "[data-role='product_title']"
        );
        titleElement.textContent = title;

        const swiperElement = drawerModal.querySelector(
            ".cart-drawer__product-modal-swiper"
        );
        const swiperWrapper = swiperElement.querySelector(".swiper-wrapper");
        swiperWrapper.innerHTML = ""; // Clear previous images
        const thumbsSwiperElement = drawerModal.querySelector(
            ".cart-drawer__product-modal-thumbs-swiper"
        );
        const thumbsSwiperWrapper =
            thumbsSwiperElement.querySelector(".swiper-wrapper");
        thumbsSwiperWrapper.innerHTML = ""; // Clear previous thumbnails
        images.forEach((image) => {
            const swiperSlide = document.createElement("div");
            swiperSlide.classList.add("swiper-slide");
            const imgElement = document.createElement("img");
            imgElement.src = image;
            imgElement.alt = title;
            const skeletonLoader = document.createElement("div");
            skeletonLoader.classList.add("skeleton");
            skeletonLoader.style.height = "100%";
            imgElement.onload = function() {
                skeletonLoader.remove();
            };
            swiperSlide.appendChild(imgElement);
            swiperSlide.appendChild(skeletonLoader);
            swiperWrapper.appendChild(swiperSlide);

            const thumbSlide = document.createElement("div");
            thumbSlide.classList.add("swiper-slide");
            const thumbImgElement = document.createElement("img");

            thumbImgElement.src = image;
            thumbImgElement.alt = title;
            const thumbSkeletonLoader = document.createElement("div");
            thumbSkeletonLoader.classList.add("skeleton");
            thumbSkeletonLoader.style.height = "100%";
            thumbSlide.appendChild(thumbImgElement);
            thumbSlide.appendChild(thumbSkeletonLoader);
            thumbImgElement.onload = function() {
                thumbSkeletonLoader.remove();
            };
            thumbsSwiperWrapper.appendChild(thumbSlide);
        });
        const thumbsSwiper = new Swiper(thumbsSwiperElement, {
            slidesPerView: 4,
            spaceBetween: 10,
            watchSlidesProgress: true,
            breakpoints: {
                640: {
                    slidesPerView: 5,
                },
                768: {
                    slidesPerView: 6,
                },
            },
        });
        const swiper = new Swiper(swiperElement, {
            slidesPerView: 1,
            spaceBetween: 10,
            navigation: {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            },
            thumbs: {
                swiper: thumbsSwiper,
            },
            watchSlidesProgress: true,
        });
    }

    function openUpsellItemModal() {
        drawerModal.classList.remove("hidden");
        setTimeout(() => {
            drawerModal.classList.add("visible");
        }, 10);
    }

    drawerModal.addEventListener("mousedown", function(e) {
        if (e.target === drawerModal) {
            closeUpsellItemModal();
        }
    });

    drawerModal.addEventListener("touchstart", function(e) {
        if (e.target === drawerModal) {
            closeUpsellItemModal();
        }
    });

    const modalCloserButtons = document.querySelectorAll(
        "[data-role='close-upsell-modal']"
    );
    modalCloserButtons.forEach((button) => {
        button.addEventListener("click", function() {
            closeUpsellItemModal();
        });
    });

    function closeUpsellItemModal() {
        drawerModal.classList.remove("visible");
        setTimeout(() => {
            drawerModal.classList.add("hidden");
        }, 200);
    }

    function isWarrantyInCart() {
        const warrantyInCart = cartState.currentCart.items.find((item) => {
            return warrantyProductIds.includes(item.id);
        });
        return !!warrantyInCart;
    }

    /**
     * This function is called when the user clicks on the "Add to Cart" button on the warranty page.
     * @function validateWarrantyAddition
     * @returns {boolean} - Returns true if the warranty can be added to the cart, false otherwise.
     * @description This function checks if the clicked item is a warranty product and if there is already a warranty in the cart.
     */
    function validateWarrantyAddition(e) {
        const clickedItemId = e.target.dataset.atc;
        const isClickedItemWarranty = warrantyProductIds.includes(
            parseInt(clickedItemId)
        );
        if (!isClickedItemWarranty) return true;

        const warrantyInCart = isWarrantyInCart();
        if (!warrantyInCart) return true;

        e.preventDefault();
        const warrantPageWarningElement = document.querySelector(
            ".warranty-page-warranty-warning"
        );
        const buttonLoader = e.target.querySelector(".loader-container");
        const buttonText = e.target.querySelector(".text");
        setTimeout(() => {
            buttonLoader.classList.add("hidden");
            buttonText.style.opacity = 1;
        }, 500);
        warrantPageWarningElement.style.display = "flex";
        setTimeout(() => {
            warrantPageWarningElement.style.display = "none";
        }, 4000);
        return false;
    }

    function validateWarrantyForm() {
        const form = document.querySelector(
            ".cart-drawer__warranty-form-container"
        );
        const formData = new FormData(form);

        let isAnyFormFieldEmpty = false;
        formData.forEach((value, key) => {
            const isValueEmpty = value.trim() === "";
            if (isValueEmpty) {
                highlightInvalidField(key);
                isAnyFormFieldEmpty = true;
            }
        });
        return !isAnyFormFieldEmpty;

        function highlightInvalidField(name) {
            const field = form.querySelector(`[name="${name}"]`);
            const wrapper = field.closest(".cart-drawer__warranty-input-container");
            wrapper.classList.add("invalid-field");
            setTimeout(() => {
                wrapper.classList.remove("invalid-field");
            }, 2000);
        }
    }

    function handleInsuranceCheckboxChange(e) {
        e.preventDefault();
        e.target.disabled = true;
        e.target
            .closest(".toggle-switch")
            .querySelector(".loader-container")
            .classList.remove("hidden");
        if (e.target.checked) {
            addShippingInsuranceToCart();
        } else {
            removeShippingInsuranceFromCart();
        }
    }

    function getShouldAddInsuranceAutomatically() {
        const {
            isCartEmpty,
            isInsuranceAdded
        } = addInsuranceAutomaticallyState;

        if (!isCartEmpty) return false;
        if (isCartEmpty && isInsuranceAdded) return false;

        addInsuranceAutomaticallyState.isInsuranceAdded = true;
        return true;
    }

    function addShippingInsuranceToCart() {
        if (!isInsuranceEnabled) return;
        const formData = {
            items: [{
                id: INSURANCE_PRODUCT_ID,
                quantity: 1,
            }, ],
        };
        return fetch(`${Shopify.routes.root}cart/add.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify(formData),
            })
            .then((response) => response.json())
            .then((data) => {
                const insuranceCheckbox = document.querySelector(
                    "input[name='shipping-insurance']"
                );
                if (insuranceCheckbox) insuranceCheckbox.checked = true;
                return updateCartHTML();
            })
            .catch((error) => {
                console.error("Error:", error);
            });
    }

    function removeShippingInsuranceFromCart() {
        return removeItemFromCart(INSURANCE_PRODUCT_ID, true).then(() => {
            const insuranceCheckbox = document.querySelector(
                "input[name='shipping-insurance']"
            );
            if (insuranceCheckbox) insuranceCheckbox.checked = false;
            return updateCartHTML();
        });
    }

    async function removeItemFromCart(lineItemId, preventUpdateHtml = false) {
        const formData = {
            updates: {
                [lineItemId]: 0,
            },
        };
        return fetch(`${Shopify.routes.root}cart/update.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify(formData),
            })
            .then((response) => response.json())
            .then((data) => {
                if (!preventUpdateHtml) {
                    updateCartHTML();
                }
            })
            .catch((error) => {
                console.error("Error:", error);
            });
    }

    async function addItemToCart(itemId, quantity, properties) {
        const formData = {
            items: [{
                id: itemId,
                quantity: quantity,
                properties: properties || undefined,
            }, ],
        };
        return fetch(`${Shopify.routes.root}cart/add.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
                body: JSON.stringify(formData),
            })
            .then((response) => response.json())
            .then((data) => {
                updateCartHTML();
            })
            .catch((error) => {
                console.error("Error:", error);
            });
    }

    function openDrawer() {
        const drawerInner = document.querySelector(".cart-drawer__inner");
        drawerOuter.style.visibility = "visible";
        drawerInner.classList.add("visible");
        document.body.style.overflow = "hidden";
    }

    function closeDrawer() {
        const drawerInner = document.querySelector(".cart-drawer__inner");
        drawerInner.classList.remove("visible");
        setTimeout(() => {
            document.body.style.overflow = "";
            drawerOuter.style.visibility = "hidden";
        }, 200);
    }
});